import { Select, Space, Tag, Typography, message } from "antd";
import { getCustomersBySearch } from "api/coreapi/customers";
import { CustomerStatuses } from "constants/customers";
import { useCallback, useEffect, useRef, useState } from "react";
import { CustomerOverview } from "shared/models/customerOverviewModel";

interface InternalCustomerSelectorProps {
    value?: CustomerOverview;
    onChange: (customer?: CustomerOverview) => void;
}

const InternalCustomerSelector = ({ value, onChange }: InternalCustomerSelectorProps) => {
    const [customers, setCustomers] = useState<CustomerOverview[]>(value ? [value] : []);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const searchTimer = useRef<NodeJS.Timeout>();

    const loadCustomers = useCallback(async (text: string, pageNumber: number, replace: boolean) => {
        setLoading(true);
        try {
            const response = await getCustomersBySearch({
                text,
                pageNumber,
                pageSize: 15,
                orderByColumns: ["Name"],
                orderByDirection: 0,
            });
            const nextCustomers = response.result.data;
            setCustomers(current => {
                const base = replace ? [] : current;
                const selected = value && !nextCustomers.some(customer => customer.id === value.id) ? [value] : [];
                return [...base, ...selected, ...nextCustomers]
                    .filter((customer, index, all) => all.findIndex(candidate => candidate.id === customer.id) === index);
            });
            setPage(response.result.page);
            setTotalPages(response.result.totalPages);
        }
        catch {
            message.error("Error obteniendo información de clientes.");
        }
        finally {
            setLoading(false);
        }
    }, [value]);

    useEffect(() => {
        loadCustomers("", 1, true);
    }, [loadCustomers]);

    useEffect(() => {
        if (value) {
            setCustomers(current => current.some(customer => customer.id === value.id) ? current : [value, ...current]);
        }
    }, [value]);

    const handleSearch = (text: string) => {
        setSearchText(text);
        if (searchTimer.current) {
            clearTimeout(searchTimer.current);
        }

        searchTimer.current = setTimeout(() => loadCustomers(text, 1, true), 400);
    };

    const handlePopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const target = event.currentTarget;
        const reachedBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 8;
        if (reachedBottom && !loading && page < totalPages) {
            loadCustomers(searchText, page + 1, false);
        }
    };

    return (
        <Select
            aria-label="Cliente"
            showSearch
            allowClear
            filterOption={false}
            loading={loading}
            placeholder="Buscar por nombre o identificación"
            value={value?.id}
            onSearch={handleSearch}
            onPopupScroll={handlePopupScroll}
            onClear={() => onChange(undefined)}
            onChange={(customerId) => onChange(customers.find(customer => customer.id === customerId))}
            style={{ width: "100%" }}
            options={customers.map(customer => ({
                value: customer.id,
                disabled: customer.customerStatusId === CustomerStatuses.Inactive,
                label: (
                    <Space direction="vertical" size={0}>
                        <Typography.Text strong>{customer.name}</Typography.Text>
                        <Typography.Text type="secondary">
                            {customer.identificationNumberTypeName}: {customer.identificationNumber}
                        </Typography.Text>
                        <Space size="small">
                            <Tag>{customer.customerStatusName}</Tag>
                            <Tag color={customer.isRegistered ? "green" : "gold"}>
                                {customer.isRegistered ? "Con cuenta" : "Sin cuenta"}
                            </Tag>
                        </Space>
                    </Space>
                ),
            }))}
        />
    );
};

export default InternalCustomerSelector;
